import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { CpiDashboardData, formatNumber, formatCurrency } from './types';
import './CpiSummaryCards.scss';

const formatLastCalculated = (dateStr: string | null): string => {
  if (!dateStr) return 'N/A';
  try {
    const date = parseISO(dateStr);
    return format(date, 'dd MMM yyyy, HH:mm');
  } catch {
    return 'N/A';
  }
};

/** kg -> tons, matching the headline card's decimal rule. */
const formatTons = (kg: number | null): string => {
  if (kg === null || kg === undefined) return '-';
  const tons = kg / 1000;
  return formatNumber(tons, tons !== 0 && Math.abs(tons) < 1 ? 3 : 0);
};

/** kg -> indicative euro cost, same formula the emissions matrix uses. */
const formatIndicativeCost = (kg: number | null, pricePerTon: number): string => {
  if (kg === null || kg === undefined) return '-';
  return formatNumber((kg / 1000) * pricePerTon, 0);
};

/**
 * Euro sign in front of an amount. Placeholder dashes stay bare — "€ -" reads
 * as a price of nothing rather than as missing data.
 */
const EURO = '€ ';
const withEuro = (amount: string): string => (amount === '-' ? amount : `${EURO}${amount}`);

interface ValueProps {
  amount: string;
  /** unit caption rendered behind the amount, e.g. "ton" */
  unit?: string;
}

/**
 * The headline number. The amount keeps an element of its own so the currency
 * prefix and the unit caption can each be styled without touching the digits.
 */
const Value: FC<ValueProps> = ({ amount, unit }) => (
  <div className="cpi-summary-cards__value" data-testid="cpi-card-value">
    <span className="cpi-summary-cards__amount">{amount}</span>
    {unit && (
      <span className="cpi-summary-cards__unit" data-testid="cpi-card-unit">
        {unit}
      </span>
    )}
  </div>
);

interface SplitProps {
  transportLabel: string;
  hubLabel: string;
  transportValue: string;
  hubValue: string;
}

/**
 * Transport / Hub on a single caption-weight line under the headline. The
 * emissions matrix now lives inside the Emissions sub-tab, so this line is
 * what carries the split onto the Operational and Energy sub-tabs.
 */
const Split: FC<SplitProps> = ({ transportLabel, hubLabel, transportValue, hubValue }) => (
  <div className="cpi-summary-cards__split" data-testid="cpi-card-split">
    <span className="cpi-summary-cards__split-item">
      <span className="cpi-summary-cards__split-label">{transportLabel}</span>{' '}
      <span className="cpi-summary-cards__split-value" data-testid="breakdown-transport">
        {transportValue}
      </span>
    </span>
    <span className="cpi-summary-cards__split-sep" aria-hidden="true">&middot;</span>
    <span className="cpi-summary-cards__split-item">
      <span className="cpi-summary-cards__split-label">{hubLabel}</span>{' '}
      <span className="cpi-summary-cards__split-value" data-testid="breakdown-hub">
        {hubValue}
      </span>
    </span>
  </div>
);

interface HeadlineProps {
  value: string;
  label: ReactNode;
  unit?: string;
}

const Headline: FC<HeadlineProps> = ({ value, label, unit }) => (
  <div className="cpi-summary-cards__headline">
    <Value amount={value} unit={unit} />
    <div className="cpi-summary-cards__label" data-testid="cpi-card-label">{label}</div>
  </div>
);

interface CardProps {
  testId?: string;
  modifier?: string;
  children: ReactNode;
}

const Card: FC<CardProps> = ({ testId, modifier, children }) => (
  <div
    className={`cpi-summary-cards__card${modifier ? ` cpi-summary-cards__card--${modifier}` : ''}`}
    data-testid={testId}
  >
    {children}
  </div>
);

interface CpiSummaryCardsProps {
  data: CpiDashboardData | null;
  isLoading: boolean;
}

const CpiSummaryCards: FC<CpiSummaryCardsProps> = ({ data, isLoading }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="cpi-summary-cards">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="cpi-summary-cards__card cpi-summary-cards__card--loading">
            <div className="cpi-summary-cards__skeleton cpi-summary-cards__skeleton--value" />
            <div className="cpi-summary-cards__skeleton cpi-summary-cards__skeleton--label" />
          </div>
        ))}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const transportWtwKg = data.transportEmissions?.wtwKg ?? null;
  const hubWtwKg = data.hubEmissions?.wtwKg ?? null;
  const co2Price = data.co2PricePerTon ?? 0;

  return (
    <div className="cpi-summary-cards">
      {/* Last Calculated timestamp */}
      <div className="cpi-summary-cards__last-calculated">
        {t('cpi.lastCalculated')}: {formatLastCalculated(data.lastCalculatedAt)}
      </div>

      {/* Card 1: Indicative Costs — headline is the Total row of the matrix */}
      <Card testId="cpi-card-indicative-costs">
        <Headline
          value={withEuro(formatNumber(data.indicativeCostEuros, 0))}
          label={
            <>
              {t('cpi.indicativeCosts')}{' '}
              <span className="cpi-summary-cards__label-rate">
                &middot; {t('cpi.ratePerTonShort', { rate: formatCurrency(co2Price) })}
              </span>
            </>
          }
        />
        <Split
          transportLabel={t('cpi.transport')}
          hubLabel={t('cpi.hub')}
          transportValue={withEuro(formatIndicativeCost(transportWtwKg, co2Price))}
          hubValue={withEuro(formatIndicativeCost(hubWtwKg, co2Price))}
        />
      </Card>

      {/* Card 2: Total CO2 WTW in tons */}
      <Card testId="cpi-card-total-wtw">
        <Headline
          value={formatNumber(data.totalEmissionWtwTons, (data.totalEmissionWtwTons ?? 0) < 1 ? 3 : 0)}
          label={t('cpi.totalEmissionWtw')}
          unit={t('cpi.unit.ton')}
        />
        <Split
          transportLabel={t('cpi.transport')}
          hubLabel={t('cpi.hub')}
          transportValue={formatTons(transportWtwKg)}
          hubValue={formatTons(hubWtwKg)}
        />
      </Card>

      {/* Card 3: CPI — CO2e per ton.km WTW */}
      <Card testId="cpi-card-cpi">
        <Headline
          value={formatNumber(data.avgCpiWtw, 3)}
          label={t('cpi.co2ePerTonKm')}
          unit={t('cpi.unit.tonKmWtw')}
        />
      </Card>

      {/* Card 4: CO2e per shipped ton + Total shipped weight */}
      <Card modifier="double">
        <div className="cpi-summary-cards__section" data-testid="cpi-card-per-ton">
          <Value amount={formatNumber(data.co2ePerTonWtw, 2)} unit={t('cpi.unit.kgWtw')} />
          <div className="cpi-summary-cards__label" data-testid="cpi-card-label">
            {t('cpi.co2ePerTon')}
          </div>
        </div>
        <div className="cpi-summary-cards__divider" />
        <div className="cpi-summary-cards__section" data-testid="cpi-card-total-shipped">
          <Value amount={formatNumber(data.totalWeightTons, 0)} unit={t('cpi.unit.ton')} />
          <div className="cpi-summary-cards__label" data-testid="cpi-card-label">
            {t('cpi.totalShipped')}
          </div>
        </div>
      </Card>

      {/* Card 5: Number of orders */}
      <Card testId="cpi-card-orders">
        <Headline value={formatNumber(data.businessOrderCount, 0)} label={t('cpi.numberOfOrders')} />
      </Card>
    </div>
  );
};

export default CpiSummaryCards;
